import {
	Building2Icon,
	ChevronsUpDownIcon,
	CreditCardIcon,
	ExternalLinkIcon,
	KeyRoundIcon,
	LayoutDashboardIcon,
	LogOutIcon,
	type LucideIcon,
	MonitorIcon,
	MoonIcon,
	PackageIcon,
	ScrollTextIcon,
	ShieldIcon,
	SunIcon,
	UsersIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { Suspense, useEffect, useState } from "react";
import { Link, Outlet, useMatch } from "react-router-dom";
import { useAuth } from "@/auth/auth-provider";
import { LogoMark } from "@/components/brand/logo";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import { PageLoader } from "@/components/status";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { appUrl, cn, initials } from "@/lib/utils";
import { type PreloadablePage, pages, preloadWhenIdle } from "@/routes/pages";

type NavItem = { label: string; path: string; icon: LucideIcon; page: PreloadablePage };

const navigation: NavItem[] = [
	{ label: "Overview", path: "/", icon: LayoutDashboardIcon, page: pages.overview },
	{ label: "Tenants", path: "/tenants", icon: Building2Icon, page: pages.tenants },
	{ label: "Users", path: "/users", icon: UsersIcon, page: pages.users },
	{ label: "Admins", path: "/admins", icon: ShieldIcon, page: pages.admins },
	{ label: "Plans & packs", path: "/catalog", icon: PackageIcon, page: pages.catalog },
	{ label: "Payments", path: "/payments", icon: CreditCardIcon, page: pages.payments },
	{ label: "Audit log", path: "/audit", icon: ScrollTextIcon, page: pages.audit },
];

function SidebarLink({ item }: { item: NavItem }) {
	const Icon = item.icon;
	const active = !!useMatch({ path: item.path, end: item.path === "/" });
	return (
		<Link
			to={item.path}
			aria-current={active ? "page" : undefined}
			onPointerEnter={() => void item.page.preload()}
			onFocus={() => void item.page.preload()}
			className={cn(
				"flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] text-sidebar-foreground outline-none transition-colors duration-75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-ring/30",
				active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
			)}
		>
			<Icon className="size-4 shrink-0 opacity-80" />
			<span className="truncate">{item.label}</span>
		</Link>
	);
}

function UserMenu() {
	const { user, logout } = useAuth();
	const { theme, setTheme } = useTheme();
	const [changingPassword, setChangingPassword] = useState(false);
	if (!user) return null;
	const themes = [
		{ value: "light", label: "Light", icon: SunIcon },
		{ value: "dark", label: "Dark", icon: MoonIcon },
		{ value: "system", label: "System", icon: MonitorIcon },
	];
	return (
		<>
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger className="flex h-10 w-full items-center gap-2 rounded-md px-2 text-left outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/30">
					<Avatar className="size-6">
						<AvatarFallback className="text-[10px]">{initials(user.name)}</AvatarFallback>
					</Avatar>
					<span className="flex min-w-0 flex-1 flex-col">
						<span className="truncate text-[13px] font-medium">{user.name}</span>
						<span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
					</span>
					<ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />
				</DropdownMenuTrigger>
				<DropdownMenuContent
					side="top"
					align="start"
					className="w-(--radix-dropdown-menu-trigger-width)"
				>
					<DropdownMenuLabel className="mono-label">Theme</DropdownMenuLabel>
					{themes.map(({ value, label, icon: Icon }) => (
						<DropdownMenuItem
							key={value}
							onSelect={() => setTheme(value)}
							className={cn(theme === value && "font-medium")}
						>
							<Icon />
							{label}
						</DropdownMenuItem>
					))}
					<DropdownMenuSeparator />
					<DropdownMenuItem asChild>
						<a href={appUrl} target="_blank" rel="noreferrer">
							<ExternalLinkIcon />
							Open Gridcast app
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem onSelect={() => setChangingPassword(true)}>
						<KeyRoundIcon />
						Change password
					</DropdownMenuItem>
					<DropdownMenuItem onSelect={logout}>
						<LogOutIcon />
						Sign out
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
			<ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
		</>
	);
}

export function AdminLayout() {
	useEffect(() => preloadWhenIdle(navigation.map((item) => item.page)), []);
	return (
		<div className="flex h-dvh bg-sidebar">
			<aside className="flex w-56 shrink-0 flex-col gap-3 px-2 pt-3 pb-2">
				<div className="flex h-7 items-center gap-2 px-2">
					<LogoMark className="size-5" />
					<span className="text-[13px] font-medium">Gridcast</span>
					<span className="rounded-sm border px-1 font-mono text-[9.5px] tracking-wider text-muted-foreground uppercase">
						Admin
					</span>
				</div>
				<nav className="flex flex-col gap-px" aria-label="Admin">
					<span className="mono-label px-2 pt-2 pb-1">Platform</span>
					{navigation.map((item) => (
						<SidebarLink key={item.path} item={item} />
					))}
				</nav>
				<div className="mt-auto">
					<UserMenu />
				</div>
			</aside>
			<main className="my-2 mr-2 flex min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] border bg-card">
				<Suspense fallback={<PageLoader />}>
					<Outlet />
				</Suspense>
			</main>
		</div>
	);
}

export function AuthLayout() {
	return (
		<div className="bg-blueprint relative flex min-h-dvh flex-col items-center justify-center px-4 py-12">
			<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,var(--background)_75%)]" />
			<div className="relative flex w-full max-w-[380px] flex-col items-center gap-8">
				<div className="flex items-center gap-2.5">
					<LogoMark className="size-7" />
					<span className="text-lg font-medium tracking-tight">Gridcast</span>
					<span className="rounded-sm border px-1.5 py-px font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
						Admin
					</span>
				</div>
				<div className="crop-marks w-full p-px [--len:10px]">
					<div className="rounded-lg border bg-card p-6 shadow-sm">
						<Suspense fallback={<PageLoader />}>
							<Outlet />
						</Suspense>
					</div>
				</div>
				<p className="mono-label text-center">Staff only · every action is audited</p>
			</div>
		</div>
	);
}
